import React, { useRef } from 'react';
import CanvasDesignRenderer from './CanvasDesignRenderer';
import HtmlTemplateRenderer from './HtmlTemplateRenderer';

interface ContentRendererProps {
  deviceStatus: any;
  htmlTemplatePlaybackKey?: number;
}

const ContentRenderer: React.FC<ContentRendererProps> = ({
  deviceStatus,
  htmlTemplatePlaybackKey = 0,
}) => {
  const videoRef = useRef(null);
  const imageRef = useRef(null);

  if (!deviceStatus?.content) {
    return null;
  }

  const renderAsset = (asset: any) => {
    const objectFitClass = deviceStatus.objectFit === 'fill' ? 'object-cover' : 'object-contain';
    
    if (asset.type === 'video') {
      return (
        <div className="w-full h-full flex items-center justify-center">
          <video
            ref={videoRef}
            src={asset.s3Url}
            className={`w-full h-full ${objectFitClass}`}
            autoPlay
            loop
            muted
            playsInline
          >
            Your browser does not support the video tag.
          </video>
        </div>
      );
    }

    if (asset.type === 'image') {
      return (
        <div className="w-full h-full flex items-center justify-center">
          <img
            ref={imageRef}
            src={asset.s3Url}
            alt={asset.name || 'Image'}
            className={`w-full h-full ${objectFitClass}`}
          />
        </div>
      );
    }

    return (
      <div className="text-white text-center">
        Unknown asset type: {asset.type}
      </div>
    );
  };

  const renderPlaylist = (playlist: any) => {
    return <PreviewPlaylistComponent playlist={playlist} deviceStatus={deviceStatus} />;
  };

  const renderLayout = (layout: any) => {
    return <RenderLayoutComponent layout={layout} deviceStatus={deviceStatus} />;
  };

  const renderSchedule = (schedule: any) => {
    return <ScheduleRenderer schedule={schedule} deviceStatus={deviceStatus} />;
  };

  switch (deviceStatus.contentType) {
    case 'asset':
      return renderAsset(deviceStatus.content);
    case 'playlist':
      return renderPlaylist(deviceStatus.content);
    case 'layout':
      return renderLayout(deviceStatus.content);
    case 'schedule':
      return renderSchedule(deviceStatus.content);
    case 'html_template':
      return (
        <div className="fixed inset-0 h-screen w-screen bg-black">
          <HtmlTemplateRenderer
            key={htmlTemplatePlaybackKey}
            content={deviceStatus.content}
            className="h-full w-full"
          />
        </div>
      );
    case 'canvas_design':
      return (
        <CanvasDesignRenderer
          content={deviceStatus.content}
          objectFit={deviceStatus.objectFit}
        />
      );
    default:
      return (
        <div className="text-white text-center">
          Unknown content type: {deviceStatus.contentType}
        </div>
      );
  }
};

const PreviewPlaylistComponent = ({ playlist, deviceStatus }: { playlist: any; deviceStatus?: any }) => {
  const [currentAssetIndex, setCurrentAssetIndex] = React.useState(0);
  const [isPlaying, setIsPlaying] = React.useState(true);
  const [hasEnded, setHasEnded] = React.useState(false);
  const timeoutRef = React.useRef(null);
  const videoRef = React.useRef(null);
  const imageRef = React.useRef(null);

  React.useEffect(() => {
    setCurrentAssetIndex(0);
    setIsPlaying(true);
    setHasEnded(false);
    clearTimeout(timeoutRef.current);
  }, [playlist]);

  React.useEffect(() => {
    if (!playlist?.playlistAssets || playlist.playlistAssets.length === 0) {
      setHasEnded(true);
      return;
    }

    if (!isPlaying || hasEnded) return;

    const currentAsset = playlist.playlistAssets[currentAssetIndex];
    if (!currentAsset) return;

    const duration = currentAsset.displayDuration * 1000;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      if (!isPlaying) return;

      const nextIndex = currentAssetIndex + 1;

      if (nextIndex >= playlist.playlistAssets.length) {
        if (playlist.loop) {
          setCurrentAssetIndex(0);
        } else {
          setHasEnded(true);
          setIsPlaying(false);
        }
      } else {
        setCurrentAssetIndex(nextIndex);
      }
    }, duration);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [currentAssetIndex, isPlaying, playlist, hasEnded]);

  const togglePlayback = () => {
    if (hasEnded) {
      setCurrentAssetIndex(0);
      setHasEnded(false);
    }
    setIsPlaying(!isPlaying);
  };

  const renderCurrentAsset = () => {
    if (!playlist?.playlistAssets || playlist.playlistAssets.length === 0) {
      return (
        <div className="w-full h-full flex items-center justify-center bg-black">
          <div className="text-white text-center">
            No content in this playlist
          </div>
        </div>
      );
    }

    const currentAsset = playlist.playlistAssets[currentAssetIndex];
    if (!currentAsset) return null;

    if (currentAsset.layout) {
      const layoutAssets = (currentAsset.assets || [])
        .filter((a: any) => a?.asset)
        .map((a: any) => ({ position: a.position, asset: a.asset }));
      return (
        <RenderLayoutComponent
          layout={{
            layout: { type: currentAsset.layout.type, name: currentAsset.layout.name },
            assets: layoutAssets,
          }}
          deviceStatus={deviceStatus}
        />
      );
    }

    const asset = currentAsset.asset;
    if (!asset) return null;

    const objectFitClass = deviceStatus?.objectFit === 'fill' ? 'object-cover' : 'object-contain';

    if (asset.type === 'video') {
      return (
        <video
          ref={videoRef}
          src={asset.s3Url}
          className={`w-full h-full ${objectFitClass}`}
          autoPlay
          muted
          playsInline
          loop={false}
          onEnded={() => {
            if (isPlaying) {
              const nextIndex = currentAssetIndex + 1;
              if (nextIndex >= playlist.playlistAssets.length) {
                if (playlist.loop) {
                  setCurrentAssetIndex(0);
                } else {
                  setHasEnded(true);
                  setIsPlaying(false);
                }
              } else {
                setCurrentAssetIndex(nextIndex);
              }
            }
          }}
        />
      );
    }

    if (asset.type === 'image') {
      return (
        <img
          ref={imageRef}
          src={asset.s3Url}
          alt=""
          className={`w-full h-full ${objectFitClass}`}
        />
      );
    }

    return null;
  };

  if (hasEnded) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-black">
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/50 backdrop-blur-sm rounded-lg p-4 flex items-center space-x-4">
          <button
            onClick={togglePlayback}
            className="text-white hover:text-gray-300 transition-colors"
            aria-label="Restart playlist"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
          </button>
          <div className="text-white text-sm">Playlist ended</div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full relative bg-black">
      {renderCurrentAsset()}

      {/* <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/50 backdrop-blur-sm rounded-lg p-4 flex items-center space-x-4">
        <button
          onClick={togglePlayback}
          className="text-white hover:text-gray-300 transition-colors"
          aria-label={isPlaying ? 'Pause' : 'Play'}
        >
          {isPlaying ? (
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          ) : (
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h1m4 0h1m6-7a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          )}
        </button>
        <div className="text-white text-sm">
          {currentAssetIndex + 1} / {playlist.playlistAssets.length}
        </div>
      </div> */}
    </div>
  );
};

interface RenderLayoutComponentProps {
  layout: {
    layout: {
      type: string;
      name: string;
    };
    assets: Array<{
      position: string;
      asset: {
        id: string;
        type: 'image' | 'video';
        s3Url: string;
        name: string;
      };
    }>;
  };
  deviceStatus?: any;
}

const RenderLayoutComponent: React.FC<RenderLayoutComponentProps> = ({
  layout,
  deviceStatus,
}) => {
  const [isPlaying, setIsPlaying] = React.useState(true);
  const videoRefs = React.useRef<{ [key: string]: HTMLVideoElement | null }>({});
  const handlersRef = React.useRef<{ [key: string]: {
    handlePlaying: () => void;
    handlePause: () => void;
    handleError: () => void;
  } }>({});

  React.useEffect(() => {
    return () => {
      Object.entries(videoRefs.current).forEach(([position, video]) => {
        if (video) {
          const handlers = handlersRef.current[position];
          if (handlers) {
            video.removeEventListener('playing', handlers.handlePlaying);
            video.removeEventListener('pause', handlers.handlePause);
            video.removeEventListener('error', handlers.handleError);
          }
        }
      });
    };
  }, []);

  const getLayoutStyles = (layoutType: string) => {
    const baseStyles = {
      display: 'grid',
      width: '100vw',
      height: '100vh',
      backgroundColor: 'black',
    };

    switch (layoutType) {
      case 'single':
        return {
          ...baseStyles,
          gridTemplateColumns: '1fr',
          gridTemplateRows: '1fr',
          gridTemplateAreas: '"center"',
        };

      case 'split_2':
        return {
          ...baseStyles,
          gridTemplateColumns: '1fr 1fr',
          gridTemplateRows: '1fr',
          gridTemplateAreas: '"left right"',
        };

      case 'split_3':
        return {
          ...baseStyles,
          gridTemplateColumns: '1fr 1fr 1fr',
          gridTemplateRows: '1fr',
          gridTemplateAreas: '"left center right"',
        };

      case 'grid_2x2':
        return {
          ...baseStyles,
          gridTemplateColumns: '1fr 1fr',
          gridTemplateRows: '1fr 1fr',
          gridTemplateAreas: '"top_left top_right" "bottom_left bottom_right"',
        };

      case 'grid_2x3':
        return {
          ...baseStyles,
          gridTemplateColumns: '1fr 1fr 1fr',
          gridTemplateRows: '1fr 1fr',
          gridTemplateAreas:
            '"top_left top_center top_right" "bottom_left bottom_center bottom_right"',
        };

      case 'grid_2x4':
        return {
          ...baseStyles,
          gridTemplateColumns: '1fr 1fr 1fr 1fr',
          gridTemplateRows: '1fr 1fr',
          gridTemplateAreas:
            '"top_1 top_2 top_3 top_4" "bottom_1 bottom_2 bottom_3 bottom_4"',
        };

      case 'grid_3x3':
        return {
          ...baseStyles,
          gridTemplateColumns: '1fr 1fr 1fr',
          gridTemplateRows: '1fr 1fr 1fr',
          gridTemplateAreas:
            '"top_left top top_right" "left center right" "bottom_left bottom bottom_right"',
        };

      case 'header_content':
        return {
          ...baseStyles,
          gridTemplateColumns: '1fr',
          gridTemplateRows: 'auto 1fr',
          gridTemplateAreas: '"header" "main"',
        };

      default:
        return baseStyles;
    }
  };

  const getPositionStyles = (position: string) => {
    return {
      gridArea: position,
      width: '100%',
      height: '100%',
      overflow: 'hidden',
    };
  };

  const renderAsset = React.useCallback((
    asset: {
      type: 'image' | 'video';
      s3Url: string;
      name: string;
    },
    position: string
  ) => {
    if (!asset) {
      return (
        <div
          style={getPositionStyles(position)}
          className="bg-gray-900 flex items-center justify-center"
        >
          <div className="text-white text-lg">No Asset</div>
        </div>
      );
    }

    const objectFitClass = deviceStatus?.objectFit === 'fill' ? 'object-cover' : 'object-contain';

    if (asset.type === 'image') {
      return (
        <div style={getPositionStyles(position)}>
          <img
            src={asset.s3Url}
            alt={asset.name}
            className={`w-full h-full ${objectFitClass}`}
          />
        </div>
      );
    } else {
      return (
        <div style={getPositionStyles(position)} className="relative">
          <video
            ref={el => {
              if (el && el !== videoRefs.current[position]) {
                const oldVideo = videoRefs.current[position];
                if (oldVideo) {
                  const oldHandlers = handlersRef.current[position];
                  if (oldHandlers) {
                    oldVideo.removeEventListener('playing', oldHandlers.handlePlaying);
                    oldVideo.removeEventListener('pause', oldHandlers.handlePause);
                    oldVideo.removeEventListener('error', oldHandlers.handleError);
                  }
                }

                videoRefs.current[position] = el;

                const handlePlaying = () => {};
                const handlePause = () => {};
                const handleError = () => {};

                handlersRef.current[position] = {
                  handlePlaying,
                  handlePause,
                  handleError,
                };

                el.addEventListener('playing', handlePlaying);
                el.addEventListener('pause', handlePause);
                el.addEventListener('error', handleError);
              }
            }}
            src={asset.s3Url}
            className={`w-full h-full ${objectFitClass}`}
            autoPlay={isPlaying}
            muted
            loop
            playsInline
            preload="metadata"
          />
        </div>
      );
    }
  }, [deviceStatus?.objectFit, isPlaying]);

  const layoutStyles = getLayoutStyles(layout.layout.type);

  return (
    <div className="w-screen h-screen relative">
      <div style={layoutStyles}>
        {layout.assets.map(({ position, asset }) => (
          <React.Fragment key={`${position}-${asset.id}`}>
            {renderAsset(asset, position)}
          </React.Fragment>
        ))}
      </div>


    </div>
  );
};

// Schedule-related components
interface ScheduleRendererProps {
  schedule: {
    id: string;
    name: string;
    fillerContentType: 'asset' | 'layout' | 'playlist';
    fillerLayoutId?: string;
    fillerPlaylistId?: string;
    fillerAssetId?: string;
    fillerLayout?: any;
    fillerPlaylist?: any;
    fillerAsset?: any;
    scheduleDates: Array<{
      id: string;
      date: string;
      timeSlots: Array<{
        id: string;
        timeFrom: string;
        timeTo: string;
        contentType: 'asset' | 'layout' | 'playlist';
        layoutId?: string;
        playlistId?: string;
        assetId?: string;
        colorCode?: string;
        layout?: any;
        playlist?: any;
        asset?: any;
      }>;
    }>;
  };
  deviceStatus?: any;
}

const ScheduleRenderer: React.FC<ScheduleRendererProps> = ({ schedule, deviceStatus }) => {
  const [currentContent, setCurrentContent] = React.useState<any>(null);
  const [currentContentType, setCurrentContentType] = React.useState<
    'asset' | 'layout' | 'playlist'
  >('asset');

  React.useEffect(() => {
    if (!String.prototype.padStart) {
      String.prototype.padStart = function(targetLength: number, padString: string = ' ') {
        targetLength = targetLength >> 0;
        padString = String(padString);
        if (this.length > targetLength) {
          return String(this);
        } else {
          targetLength = targetLength - this.length;
          if (targetLength > padString.length) {
            padString += padString.repeat(targetLength / padString.length);
          }
          return padString.slice(0, targetLength) + String(this);
        }
      };
    }

    const parseTime = (timeString: string): number => {
      try {
        const parts = timeString.split(':');
        if (parts.length >= 2) {
          const hours = parseInt(parts[0], 10) || 0;
          const minutes = parseInt(parts[1], 10) || 0;
          const seconds = parts.length > 2 ? (parseInt(parts[2], 10) || 0) : 0;
          return hours * 3600 + minutes * 60 + seconds;
        }
        return 0;
      } catch (error) {
        return 0;
      }
    };

    const getCurrentTimeInSeconds = (): number => {
      try {
        const now = new Date();
        const hours = now.getHours();
        const minutes = now.getMinutes();
        const seconds = now.getSeconds();
        return hours * 3600 + minutes * 60 + seconds;
      } catch (error) {
        try {
          const fallbackNow = new Date();
          const timeStr = fallbackNow.toString();
          const timeMatch = timeStr.match(/(\d{1,2}):(\d{2}):(\d{2})/);
          if (timeMatch) {
            const h = parseInt(timeMatch[1], 10) || 0;
            const m = parseInt(timeMatch[2], 10) || 0;
            const s = parseInt(timeMatch[3], 10) || 0;
            return h * 3600 + m * 60 + s;
          }
        } catch (fallbackError) {
        }
        return 0;
      }
    };

    const getCurrentDate = (): string => {
      try {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      } catch (error) {
        return '';
      }
    };

    const updateCurrentContent = () => {
      try {
        const currentDate = getCurrentDate();
        const currentTimeInSeconds = getCurrentTimeInSeconds();

        const todaySchedule = schedule.scheduleDates.find(
          scheduleDate => scheduleDate.date === currentDate
        );

        if (todaySchedule && todaySchedule.timeSlots && todaySchedule.timeSlots.length > 0) {
          const activeTimeSlot = todaySchedule.timeSlots.find(timeSlot => {
            try {
              const timeFromSeconds = parseTime(timeSlot.timeFrom);
              const timeToSeconds = parseTime(timeSlot.timeTo);
              
              if (timeToSeconds < timeFromSeconds) {
                const isActive = currentTimeInSeconds >= timeFromSeconds || currentTimeInSeconds <= timeToSeconds;
                return isActive;
              }
              
              const isActive = currentTimeInSeconds >= timeFromSeconds && currentTimeInSeconds <= timeToSeconds;
              return isActive;
            } catch (error) {
              return false;
            }
          });

          if (activeTimeSlot) {
            setCurrentContentType(activeTimeSlot.contentType);
            switch (activeTimeSlot.contentType) {
              case 'asset':
                setCurrentContent(activeTimeSlot.asset);
                break;
              case 'layout':
                setCurrentContent(activeTimeSlot.layout);
                break;
              case 'playlist':
                setCurrentContent(activeTimeSlot.playlist);
                break;
            }
            return;
          }
        }

        setCurrentContentType(schedule.fillerContentType);
        switch (schedule.fillerContentType) {
          case 'asset':
            setCurrentContent(schedule.fillerAsset);
            break;
          case 'layout':
            setCurrentContent(schedule.fillerLayout);
            break;
          case 'playlist':
            setCurrentContent(schedule.fillerPlaylist);
            break;
        }
      } catch (error) {
        setCurrentContentType(schedule.fillerContentType);
        switch (schedule.fillerContentType) {
          case 'asset':
            setCurrentContent(schedule.fillerAsset);
            break;
          case 'layout':
            setCurrentContent(schedule.fillerLayout);
            break;
          case 'playlist':
            setCurrentContent(schedule.fillerPlaylist);
            break;
        }
      }
    };

    updateCurrentContent();

    const interval = setInterval(updateCurrentContent, 10000);
    const fastInterval = setInterval(updateCurrentContent, 1000);
    setTimeout(() => clearInterval(fastInterval), 60000);

    return () => {
      clearInterval(interval);
      clearInterval(fastInterval);
    };
  }, [schedule]);

  if (!currentContent) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-black">
        <div className="text-white text-center">
          <h3 className="text-2xl font-bold mb-2">No Content Available</h3>
          <p className="text-lg">Schedule: {schedule.name}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full relative">
      <ContentTypeRenderer
        content={currentContent}
        contentType={currentContentType}
        deviceStatus={deviceStatus}
      />
    </div>
  );
};

interface ContentTypeRendererProps {
  content: any;
  contentType: 'asset' | 'layout' | 'playlist';
  deviceStatus?: any;
}

const ContentTypeRenderer: React.FC<ContentTypeRendererProps> = ({
  content,
  contentType,
  deviceStatus,
}) => {
  switch (contentType) {
    case 'asset':
      return <AssetRenderer asset={content} deviceStatus={deviceStatus} />;
    case 'layout':
      return <RenderLayoutComponent layout={content} deviceStatus={deviceStatus} />;
    case 'playlist':
      return <PreviewPlaylistComponent playlist={content} deviceStatus={deviceStatus} />;
    default:
      return (
        <div className="w-full h-full flex items-center justify-center bg-black">
          <div className="text-white text-center">
            <p>Unknown content type: {contentType}</p>
          </div>
        </div>
      );
  }
};

interface AssetRendererProps {
  asset: {
    id: string;
    name: string;
    type: 'image' | 'video';
    s3Url: string;
    description?: string;
    status: string;
  };
  deviceStatus?: any;
}

const AssetRenderer: React.FC<AssetRendererProps> = ({ asset, deviceStatus }) => {
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const imageRef = React.useRef<HTMLImageElement>(null);
  const objectFitClass = deviceStatus?.objectFit === 'fill' ? 'object-cover' : 'object-contain';

  if (asset.type === 'video') {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <video
          ref={videoRef}
          src={asset.s3Url}
          className={`w-full h-full ${objectFitClass}`}
          autoPlay
          loop
          muted
          playsInline
        >
          Your browser does not support the video tag.
        </video>
      </div>
    );
  }

  if (asset.type === 'image') {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <img
          ref={imageRef}
          src={asset.s3Url}
          alt={asset.name || 'Image'}
          className={`w-full h-full ${objectFitClass}`}
        />
      </div>
    );
  }

  return (
    <div className="w-full h-full flex items-center justify-center bg-black">
      <div className="text-white text-center">
        <p>Unknown asset type: {asset.type}</p>
      </div>
    </div>
  );
};

export default ContentRenderer;
